import { localized, type AiToolDefinition } from '@/config/ai-tools';

/**
 * schema.org objects for a generator page: the app with its credit-pack
 * price range, its FAQ, and the Home › Tools › Tool breadcrumb.
 * Pure — URLs and labels come in already localized.
 */
export function buildToolStructuredData(params: {
  tool: AiToolDefinition;
  locale: string;
  appName: string;
  siteUrl: string;
  toolsLabel: string;
  packPricesUsd: readonly number[];
  absoluteUrl: (path: string) => string;
}): object[] {
  const { tool, locale, appName, siteUrl, toolsLabel, packPricesUsd, absoluteUrl } = params;
  const t = (value: Parameters<typeof localized>[0]) => localized(value, locale);
  const url = absoluteUrl(`/tools/${tool.slug}`);

  const app: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: t(tool.title),
    url,
    description: t(tool.seoDescription),
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web',
    dateModified: tool.updatedAt,
    publisher: { '@type': 'Organization', name: appName, url: siteUrl },
  };
  if (packPricesUsd.length > 0) {
    app.offers = {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: Math.min(...packPricesUsd).toFixed(2),
      highPrice: Math.max(...packPricesUsd).toFixed(2),
      offerCount: packPricesUsd.length,
    };
  }

  return [
    app,
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: tool.faqs.map((faq) => ({
        '@type': 'Question',
        name: t(faq.q),
        acceptedAnswer: { '@type': 'Answer', text: t(faq.a) },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: appName, item: absoluteUrl('/') },
        { '@type': 'ListItem', position: 2, name: toolsLabel, item: absoluteUrl('/tools') },
        { '@type': 'ListItem', position: 3, name: t(tool.name), item: url },
      ],
    },
  ];
}
